import {
  ApiError,
  OfChatMessagesService,
  type OnlyFansMessage,
  SendMessageRequest,
} from '@api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
} from 'effector'
import { useUnit } from 'effector-react'
import _ from 'lodash/fp'

import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import {
  $fanId,
  markAllChatsAsReadFx,
} from '@/models/chattingUI/chats/chats.ts'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import { chatsService } from '@/modules/chatting/service/chats.ts'
import { formatMessageMedia } from '@/modules/chatting/state/utils.ts'
import type {
  GetMessagesParams,
  GetMessagesResponse,
  Message,
  MessageItem,
  MessagesStackDirection,
  SendMessageParams,
} from '@/modules/chatting/types.ts'
import {
  BlacklistedWordDetectionError,
  getBlacklistedWordMatch,
} from '@/modules/chatting/utils/blacklistedWords.ts'
import { getErrorMessage, handleError } from '@/utils/error'
import { hasNonEmptyMedia, prepareMessage } from '@/utils/messages.ts'

const noMessages: unknown[] = []

export const setMessageIdToFetch = createEvent<
  Nullable<{
    /**
     * If no fanId defined use current.
     */
    fanId?: number
    messageIdToFetch: number
  }>
>('set message id to fetch')
export const $messageIdToFetch = createStore<Nullable<number>>(null, {
  name: 'message id to fetch',
}).on(setMessageIdToFetch, (_, data) => data?.messageIdToFetch ?? null)

export const toggleMessageLikeFx = attach({
  name: 'toggle fan message like',
  source: {
    userId: $fanId,
    creatorId: $creatorId,
  },
  mapParams: (message: OnlyFansMessage, { creatorId, userId }) => ({
    messageId: message.id,
    isLiked: message.isLiked,
    creatorId: creatorId!,
    userId,
  }),
  effect: createEffect(
    async ({
      isLiked,
      ...params
    }: {
      isLiked: boolean
      creatorId: number
      messageId: number
      userId: number
    }) => {
      const result = isLiked
        ? await OfChatMessagesService.unlikeChatMessage(params)
        : await OfChatMessagesService.likeChatMessage(params)

      return {
        fanId: params.userId,
        message: result,
      }
    },
  ),
})

toggleMessageLikeFx.failData.watch((error) => {
  handleError(
    error,
    'Unexpected error occurred while liking the message, please try again later',
  )
})

// Add store to track which message is being liked
export const $messageLikeLoadingMap = createStore<Record<number, boolean>>(
  {},
  { name: 'message like loading map' },
)
  .on(toggleMessageLikeFx, (store, message) => ({
    ...store,
    [message.id]: true,
  }))
  .on(toggleMessageLikeFx.finally, (store, { params: message }) => ({
    ...store,
    [message.id]: false,
  }))

export const deleteMessageFx = attach({
  name: 'delete fan message by id',
  source: {
    userId: $fanId,
    creatorId: $creatorId,
  },
  mapParams: (messageId: number, { creatorId, userId }) => ({
    messageId,
    creatorId: creatorId!,
    userId,
  }),
  effect: createEffect(
    async (params: {
      messageId: number
      creatorId: number
      userId: number
    }) => {
      const result = await OfChatMessagesService.deleteChatMessage(params)

      return {
        fanId: params.userId,
        message: result,
      }
    },
  ),
})

deleteMessageFx.failData.watch((error) => {
  handleError(
    error,
    'Unexpected error occurred while deleting the message, please try again later',
  )
})

export const sendMessageFx = attach({
  name: 'send fan message',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (
    {
      message,
      ...rest
    }: Omit<SendMessageParams, 'creatorId'> & { optimisticMessageId?: number },
    { creatorId },
  ) => ({
    creatorId: creatorId!,
    requestBody: formatMessageMedia(message),
    ...rest,
  }),
  effect: createEffect(
    async ({
      creatorId,
      userId,
      requestBody,
      optimisticMessageId,
      chat,
    }: Omit<SendMessageParams, 'message'> & {
      requestBody: SendMessageRequest
      optimisticMessageId?: number
    }) => {
      try {
        return await OfChatMessagesService.sendMessage({
          creatorId: creatorId,
          userId: userId,
          requestBody: requestBody,
        })
      } catch (error) {
        const message = getErrorMessage(error)

        const blacklistedWordDetection = getBlacklistedWordMatch(message)

        if (blacklistedWordDetection) {
          openDialog({
            type: DIALOG_TYPE.BLACKLISTED_WORD_USAGE_DETECTION,
            match: blacklistedWordDetection,
          })

          const fanId = chat?.withUser?.id

          if (fanId && optimisticMessageId) {
            removeOptimisticMessageByFanId({
              fanId,
              messageId: optimisticMessageId,
            })
          }
          throw new BlacklistedWordDetectionError(message)
        } else {
          addOptimisticMessageError({ messageId: optimisticMessageId! })
          handleError(
            message,
            'Unexpected error occurred while sending the message, please try again later',
          )
        }
      }
    },
  ),
})

export const getMessagesFx = attach({
  name: 'get fan messages',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (
    {
      anchorMessageId,
      fanId,
      direction,
    }: {
      anchorMessageId?: number
      fanId: number
      direction: MessagesStackDirection
    },
    { creatorId },
  ) => ({
    creatorId: creatorId!,
    anchorMessageId,
    direction,
    limit: 20,
    fanId,
  }),
  effect: createEffect<
    GetMessagesParams,
    GetMessagesResponse,
    { error: ApiError; direction: MessagesStackDirection }
  >(chatsService.getMessages),
})

getMessagesFx.failData.watch(({ error }) => {
  handleError(error, 'Something went wrong while getting messages')
})

export const $messagesErrorsByFanIdMap = createStore<
  Record<
    number,
    Nullable<{ error: ApiError; direction: MessagesStackDirection }>
  >
>({}, { name: 'messages errors by fan id map' })
  .on(getMessagesFx.fail, (store, { params: { fanId }, error }) => ({
    ...store,
    [fanId]: error,
  }))
  .on(getMessagesFx, (store, { fanId }) => ({
    ...store,
    [fanId]: null,
  }))

export const $messagesError = combine(
  $fanId,
  $messagesErrorsByFanIdMap,
  (fanId, errors) => {
    return errors[fanId] ?? null
  },
)

export const $isNextPageFetchingError = $messagesError.map((error) => {
  return error?.direction === 'down'
})

export const $isPreviousPageFetchingError = $messagesError.map((error) => {
  return error?.direction === 'up'
})

export const resetMessagesByFanId = createEvent<{
  fanId: number
}>('reset messages by fan id')
export const $messagesPagesByFanIdMap = createStore<
  Record<number, GetMessagesResponse[]>
>(
  {},
  {
    name: 'message pages by fan id map',
  },
).on(resetMessagesByFanId, (store, { fanId }) => {
  return {
    ...store,
    [fanId]: noMessages as GetMessagesResponse[],
  }
})

/**
 * Selected fan message pages.
 */
export const $messagePages = combine(
  $fanId,
  $messagesPagesByFanIdMap,
  (fanId, messages) => {
    return messages[fanId] ?? noMessages
  },
)

export const $canLoadNextMessagePages = combine(
  $messagePages,
  getMessagesFx.pending,
  (pages, isLoading) => {
    return (pages.length ? pages[pages.length - 1].hasMore : true) && !isLoading
  },
)

export const $canLoadPrevMessagePages = combine(
  $messagePages,
  getMessagesFx.pending,
  (pages, isLoading) => {
    return (
      !pages.some(({ anchorMessageId }) => anchorMessageId === 0) &&
      !!pages[0]?.hasMore &&
      !isLoading
    )
  },
)

export const getConcreteMessagePageFx = attach({
  name: 'get fan concrete messages page',
  mapParams: ({
    fanId,
    messageIdToFetch,
  }: {
    fanId: number
    messageIdToFetch: number
  }) => {
    return {
      anchorMessageId: messageIdToFetch,
      direction: 'down' as const,
      fanId,
    }
  },
  effect: getMessagesFx,
})

export const getNextMessagePageFx = attach({
  name: "get fan messages' next page",
  source: {
    fanId: $fanId,
    pages: $messagePages,
  },
  mapParams: (_: void, { pages, fanId }) => {
    const lastPageMessages = pages[pages.length - 1]?.list

    return {
      anchorMessageId:
        lastPageMessages?.[lastPageMessages?.length - 1]?.id ?? 0,
      direction: 'down' as const,
      fanId,
    }
  },
  effect: getMessagesFx,
})

export const getPreviousMessagePageFx = attach({
  name: "get fan messages' prev page",
  source: {
    fanId: $fanId,
    pages: $messagePages,
  },
  mapParams: (_: void, { pages, fanId }) => {
    const isPreviousPageRequested = pages.some(
      (page) => page.direction === 'up',
    )
    const lastMessage = isPreviousPageRequested
      ? pages[0]?.list[pages[0]?.list.length - 1]
      : pages[0]?.list[0]

    return {
      anchorMessageId: lastMessage?.id ?? 0,
      direction: 'up' as const,
      fanId,
    }
  },
  effect: getMessagesFx,
})

export const addMessageByFanId = createEvent<{
  fanId: number
  message: OnlyFansMessage
}>('add message by fan id')
/**
 * Temporal message, to enable better UX.
 * It added right after user press `send` before we got server result, if could be cancelled by server and in that case we should revert it.
 * If server accept it, it would be replaced by original message.
 */
export const addOptimisticMessageByFanId = createEvent<{
  fanId: number
  message: OnlyFansMessage
}>('add optimistic message by fan id')
export const addOptimisticMessageError = createEvent<{
  messageId: number
}>('add error to optimistic message by id')
export const removeOptimisticMessageByFanId = createEvent<{
  fanId: number
  messageId: number
}>('remove optimistic message by fan / message id')

export const $messagesByFanIdMap = createStore<
  Record<number, Array<MessageItem>>
>(
  {},
  {
    name: 'messages by fan id map',
  },
)
  .on(getMessagesFx.doneData, (store, { fanId, list }) => {
    const allMessages = (store[fanId] ?? []).concat(
      list.map(({ id, createdAt }) => ({
        id,
        createdAt: new Date(createdAt).getTime(),
      })),
    )

    return {
      ...store,
      [fanId]: Object.values(
        allMessages.reduce<Record<number, MessageItem>>(
          (result, newMessage) => {
            result[newMessage.id] = newMessage

            return result
          },
          {},
        ),
      ).sort((a, b) => a.createdAt - b.createdAt),
    }
  })
  .on(
    [addMessageByFanId, addOptimisticMessageByFanId],
    (store, { fanId, message: { id, createdAt } }) => {
      const allMessages = (store[fanId] ?? []).concat({
        id,
        createdAt: new Date(createdAt).getTime(),
      })

      return {
        ...store,
        [fanId]: Object.values(
          allMessages.reduce<Record<number, MessageItem>>(
            (result, newMessage) => {
              result[newMessage.id] = newMessage

              return result
            },
            {},
          ),
        ).sort((a, b) => a.createdAt - b.createdAt),
      }
    },
  )
  .on(removeOptimisticMessageByFanId, (store, { fanId, messageId }) => {
    return {
      ...store,
      [fanId]: store[fanId].filter(({ id }) => id !== messageId),
    }
  })
  .on(resetMessagesByFanId, (store, { fanId }) => {
    return {
      ...store,
      [fanId]: noMessages as MessageItem[],
    }
  })

/**
 * Message will not affect rendering list. Usefull for pinned messages.
 */
export const addMessageToMap = createEvent<{
  message: OnlyFansMessage
}>('add message by id')
export const markAsReadById = createEvent<number>('mark message as read by id')
export const markAsUNReadById = createEvent<number>(
  'mark message as UNread by id',
)
export const $messagesByIdMap = createStore<Record<number, Message>>(
  {},
  { name: 'messages by id map' },
)
  .on(getMessagesFx.doneData, (store, { list }) => {
    return list.map(prepareMessage).reduce((result, newMessage) => {
      const oldMessage = result[newMessage.id]

      // OF returns empty media during processing, but we want to show thumb
      result[newMessage.id] =
        hasNonEmptyMedia(oldMessage) && !hasNonEmptyMedia(newMessage)
          ? { ...newMessage, media: oldMessage.media }
          : newMessage

      return result
    }, store)
  })
  .on([addMessageByFanId, addMessageToMap], (store, { message }) => {
    const oldMessage = store[message.id]

    // OF returns empty media during processing, but we want to show thumb
    return {
      ...store,
      [message.id]:
        hasNonEmptyMedia(oldMessage) && !hasNonEmptyMedia(message)
          ? prepareMessage({ ...message, media: oldMessage.media })
          : prepareMessage(message),
    }
  })
  .on(addOptimisticMessageByFanId, (store, { message }) => {
    return {
      ...store,
      [message.id]: prepareMessage(message),
    }
  })
  .on(addOptimisticMessageError, (store, { messageId }) => {
    return store[messageId]
      ? {
          ...store,
          [messageId]: {
            ...store[messageId],
            _hasError: true,
          },
        }
      : store
  })
  .on(markAllChatsAsReadFx.doneData, (store) => {
    return Object.values(store).reduce<Record<number, Message>>(
      (result, value) => {
        result[value.id] = {
          ...value,
          isReadByCreator: true,
        }

        return result
      },
      {},
    )
  })
  .on(markAsReadById, (store, messageId) => {
    const oldMessage = store[messageId]

    return oldMessage
      ? {
          ...store,
          [messageId]: {
            ...oldMessage,
            isReadByCreator: true,
          },
        }
      : store
  })
  .on(markAsUNReadById, (store, messageId) => {
    const oldMessage = store[messageId]

    return oldMessage
      ? {
          ...store,
          [messageId]: {
            ...oldMessage,
            isReadByCreator: false,
          },
        }
      : store
  })

/**
 * Selected fan messages.
 */
export const $messages = combine(
  $fanId,
  $messagesByFanIdMap,
  $messagesByIdMap,
  (fanId, messages, map) => {
    return (
      messages[fanId]?.map(({ id }) => map[id]).filter(Boolean) ?? noMessages
    )
  },
)

export const $groupedMessagesEntries = $messages.map((messages) => {
  return Object.entries(_.groupBy('sentDate', messages))
})

export const useMessageDetails = (messageId?: number) => {
  const map = useUnit($messagesByIdMap)

  return map[messageId!]
}

export const useMessageLikeLoading = (messageId: number) => {
  const loadingMap = useUnit($messageLikeLoadingMap)

  return loadingMap[messageId] ?? false
}
